import React, { useEffect, useState, useMemo } from 'react';
import { useTimeContext } from '../contexts/TimeContext';
import { DotaService } from '../services/DotaService';

interface ObjectiveEvent {
  time: number;
  type: string;
  team: string;
  lane?: string;
  key?: string;
  slot?: number;
  player_slot?: number;
  unit?: string;
  team_id?: number;
  player1?: number;
  player2?: number;
  value?: number;
  tier?: number;
  tierName?: string;
  heroId?: number;
  victimId?: number;
  teamName?: string;
  victimTeamName?: string;
}

interface GameDataLogProps {
  className?: string;
  matchId?: string;
  height?: string | number;
}

// Simple function to capitalize first letter
const capitalize = (str: string): string => str.charAt(0).toUpperCase() + str.slice(1);

const GameDataLog: React.FC<GameDataLogProps> = ({ className = '', matchId, height = 350 }) => {
  const { gameObjectives, currentTimePoint, setGameObjectives } = useTimeContext();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [heroes, setHeroes] = useState<any[]>([]);
  
  // Calculate team kill counts up to the current time point
  const { radiantKills, direKills } = useMemo(() => {
    const radiantKills = gameObjectives
      .filter(obj => 
        obj.time <= currentTimePoint && 
        obj.type === 'HERO_KILL' && 
        obj.team === 'radiant'
      ).length;
      
    const direKills = gameObjectives
      .filter(obj => 
        obj.time <= currentTimePoint && 
        obj.type === 'HERO_KILL' && 
        obj.team === 'dire'
      ).length;
      
    return { radiantKills, direKills };
  }, [gameObjectives, currentTimePoint]);

  // Load heroes data when component mounts
  useEffect(() => {
    const loadHeroes = async () => {
      try {
        const heroesData = await DotaService.getHeroes();
        setHeroes(heroesData);
      } catch (err) {
        console.error('Error loading heroes:', err);
      }
    };
    
    loadHeroes();
  }, []);

  // Fetch objectives data from API when matchId changes
  useEffect(() => {
    const fetchObjectives = async () => {
      if (!matchId) return;
      
      setLoading(true);
      setError(null);
      
      try {
        console.log('Fetching objectives for match:', matchId);
        const objectives = await DotaService.getMatchObjectives(matchId);
        
        if (objectives && objectives.length > 0) {
          console.log('Loaded objectives:', objectives);
          // Log tower kill events for debugging
          const towerKills = objectives.filter(obj => 
            obj.type === 'TOWER_KILL' || 
            (obj.type === 'BUILDING_KILL' && obj.key && obj.key.includes('tower'))
          );
          console.log('Tower kill events:', towerKills);
          
          setGameObjectives(objectives);
        } else {
          console.warn('No objectives found for match:', matchId);
        }
      } catch (err) {
        console.error('Error fetching objectives:', err);
        setError('Failed to load game events');
      } finally {
        setLoading(false);
      }
    };
    
    fetchObjectives();
  }, [matchId, setGameObjectives]);

  // Convert seconds to time format: MM:SS
  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(Math.abs(seconds) / 60);
    const remainingSeconds = Math.floor(Math.abs(seconds) % 60);
    const timeString = `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    return seconds < 0 ? `-${timeString}` : timeString;
  };

  // Clean up hero name from key
  const cleanHeroName = (key: string): string => {
    if (!key) return '';
    
    // Extract hero name from npc_dota_hero_XXX format
    if (key.includes('npc_dota_hero_')) {
      return key.replace('npc_dota_hero_', '').replace(/_/g, ' ');
    }
    
    return key;
  };

  // Get hero icon URL from hero name
  const getHeroIconUrl = (heroName: string): string | null => {
    if (!heroName || !heroes.length) return null;
    
    // Clean up hero name if it's in npc_dota_hero_XXX format
    const cleanName = heroName.includes('npc_dota_hero_') 
      ? heroName.replace('npc_dota_hero_', '') 
      : heroName.replace(/\s+/g, '_').toLowerCase();
    
    // Try to find hero by name match
    const hero = heroes.find(h => 
      h.name.toLowerCase() === heroName.toLowerCase() || 
      h.name.toLowerCase().includes(cleanName.toLowerCase()) ||
      h.imageUrl.toLowerCase().includes(cleanName.toLowerCase())
    );
    
    if (hero) {
      return hero.imageUrl;
    }
    
    // If not found by exact match, try to generate URL format based on hero name
    return `https://cdn.dota2.com/apps/dota2/images/heroes/${cleanName}_full.png`;
  };
  
  // Check if a target is a hero
  const isHeroTarget = (target: string): boolean => {
    if (!target) return false;
    
    // Check if it's a hero name format
    return target.includes('npc_dota_hero_') || 
           heroes.some(h => h.name.toLowerCase() === target.toLowerCase());
  };

  // Get visible objectives up to current time point, limited to 4 most recent items
  const visibleObjectives = gameObjectives
    .filter(objective => objective.time <= currentTimePoint)
    .sort((a, b) => b.time - a.time); // Sort descending to show latest first

  // Format event description
  const formatEventDescription = (event: ObjectiveEvent): { team: string, action: string, target: string, isHero: boolean } => {
    // Start with default values
    let team = capitalize(event.team);
    let action = 'performed action';
    let target = '';
    let isHero = false;
    
    // Check event type and format description accordingly
    switch (event.type) {
      case 'TOWER_KILL':
      case 'BARRACKS_KILL': 
      case 'ANCIENT_KILL':
      case 'BUILDING_KILL':
        // Building destruction events
        team = capitalize(event.team); // "Radiant" or "Dire" (the destroyer)
        action = 'destroyed';
        
        // The victim team is always the opposite of the team that performed the action
        const victimTeam = event.team === 'radiant' ? 'Dire' : 'Radiant';
        
        // Format building details
        let buildingDetails;
        if (event.lane && event.tierName) {
          buildingDetails = `${event.lane} ${event.tierName}`;
        } else if (event.tierName) {
          buildingDetails = event.tierName;
        } else if (event.lane) {
          buildingDetails = `${event.lane} ${event.unit || ''} barracks`;
        } else if (event.type === 'ANCIENT_KILL') {
          buildingDetails = 'Ancient';
        } else {
          buildingDetails = 'tower';
        }
        
        target = `${victimTeam} ${buildingDetails}`;
        break;
        
      case 'HERO_KILL':
        action = 'killed';
        target = event.key || '';
        isHero = true;
        break;
        
      case 'COURIER_KILL':
        action = 'killed';
        target = 'courier';
        break;
        
      case 'ROSHAN_KILL':
      case 'CHAT_MESSAGE_ROSHAN_KILL':
        action = 'defeated Roshan';
        break;
        
      case 'AEGIS':
      case 'CHAT_MESSAGE_AEGIS':
        action = 'claimed the Aegis';
        break;
        
      case 'CHAT_MESSAGE_FIRSTBLOOD':
        action = 'drew first blood';
        break;
        
      default:
        if (event.type.startsWith('CHAT_MESSAGE_')) {
          action = event.type.replace('CHAT_MESSAGE_', '').toLowerCase().replace(/_/g, ' ');
        } else {
          action = event.type.toLowerCase().replace(/_/g, ' ');
        }
    }
    
    return { team, action, target, isHero };
  };

  return (
    <div className={`game-data-log ${className}`} style={{ height: 'auto' }}>
      <div className="game-data-header">
        <h4>Game Events</h4>
        <div className="team-kills-container">
          <div className="team-kills radiant">
            <span className="team-name">Radiant</span>
            <span className="kill-count">{radiantKills}</span>
          </div>
          <span className="kills-separator">-</span>
          <div className="team-kills dire">
            <span className="kill-count">{direKills}</span>
            <span className="team-name">Dire</span>
          </div>
        </div>
      </div>
      
      {loading ? (
        <div className="loading-events">Loading events...</div>
      ) : error ? (
        <div className="error-events">Error loading events</div>
      ) : visibleObjectives.length === 0 ? (
        <div className="no-events">No events to display</div>
      ) : (
        <div 
          className="objectives-list-container" 
          style={{ 
            height: '172px', // Fixed height for 4 rows (approximately 43px per row)
            maxHeight: '172px',
            overflow: 'auto' // Changed from 'hidden' to 'auto' to enable scrolling
          }}
        >
          <ul className="objectives-list">
            {visibleObjectives.map((objective, index) => {
              const { team, action, target, isHero } = formatEventDescription(objective);
              const teamClass = objective.team === 'radiant' ? 'blue_team' : 'red_team';
              const heroIconUrl = isHero ? getHeroIconUrl(target) : null;
              
              return (
                <li 
                  key={index} 
                  className={`objective-event ${objective.type.toLowerCase().replace(/\s+/g, '_')} ${teamClass}`}
                  style={{ padding: '4px 8px', marginBottom: '2px', backgroundColor: '#18191a' }}
                >
                  <div className="event-time-bar">
                    <span className="event-time">{formatTime(objective.time)}</span>
                  </div>
                  <div className="event-icon" style={{ width: '16px', height: '16px', borderRadius: '50%', backgroundColor: '#555', marginRight: '10px' }}></div>
                  <div className="event-description">
                    <span className="event-team">{team}</span>
                    <span className="event-action">{action}</span>
                    
                    {/* Hero icon or text */}
                    {isHero && heroIconUrl ? (
                      <img 
                        src={heroIconUrl} 
                        alt={cleanHeroName(target)}
                        className="hero-icon-small"
                        title={cleanHeroName(target)}
                      />
                    ) : (
                      target && <span className="event-target">{cleanHeroName(target)}</span>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

export default GameDataLog; 